import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  TextInput,
  Select,
  Paper,
  Group,
  Button,
  Text,
  Stack,
  Tabs,
  Grid,
  Box,
  NumberInput,
  Alert,
  Title,
  LoadingOverlay,
  ActionIcon,
  Textarea,
  Accordion,
} from "@mantine/core";
import {
  IconPlus,
  IconTrash,
  IconEdit,
  IconGripVertical,
  IconInfoCircle,
  IconX,
  IconCopy,
} from "@tabler/icons-react";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import {
  getCategoryTradeInSettingDetail,
  createCategoryTradeInSetting,
  editCategoryTradeInSetting,
} from "../../../api/ApiTradeIn";
import {
  RuleCriterion,
  TradeCategoryHeader,
  TradeCategoryRule,
  mapCategoryResponseToState,
} from "./index";

// Map Frontend State back to Unified Save Payload
const mapStateToSavePayload = (cat: TradeCategoryRule) => {
  const isUpgrade =
    cat.type === "LÊN ĐỜI" ||
    cat.type === "LEN DOI" ||
    cat.categoryType === "upgrade";
  const mappedHeaders: any[] = [];

  cat.headers.forEach((h) => {
    const linesPayload = (h.tblTradeInLines || [])
      .filter((line) => line.isActive !== false)
      .map((line) => ({
        id:
          typeof line.id === "string" && line.id.startsWith("criterion-")
            ? 0
            : Number(line.id) || 0,
        description: line.text,
        percent: line.percent || null,
        amount: line.amount || null,
      }));

    mappedHeaders.push({
      id: h.id || 0,
      option: h.option,
      productCondition: h.productCondition,
      percentSupport: h.percentSupport || 0,
      amountSupport: h.amountSupport,
      shortDescription: h.shortDescription || "",
      includedAccessories: h.includedAccessories || "",
      missingAccessoryNote: h.missingAccessoryNote || "",
      tblTradeInLines: linesPayload,
    });

    const mergedIds = h.mergedIds || [];
    mergedIds.forEach((dupId) => {
      mappedHeaders.push({
        id: dupId,
        option: h.option,
        productCondition: h.productCondition,
        percentSupport: h.percentSupport || 0,
        amountSupport: h.amountSupport,
        shortDescription: h.shortDescription || "",
        includedAccessories: h.includedAccessories || "",
        missingAccessoryNote: h.missingAccessoryNote || "",
        tblTradeInLines: [], // Clean up the criteria lines for duplicate headers
      });
    });
  });

  return {
    id:
      typeof cat.id === "string" && cat.id.includes("-")
        ? 0
        : Number(cat.id) || 0,
    groupName: cat.groupName,
    linkIndex: cat.linkIndex,
    title: cat.groupName,
    url: cat.linkIndex,
    type: cat.type,
    orderNumber: cat.orderNumber || 1,
    status: cat.status === "1",
    quantity: cat.quantity || 0,
    percentSupport: isUpgrade
      ? cat.percentSupport !== undefined && cat.percentSupport !== null
        ? cat.percentSupport
        : 0
      : null,
    maxAmountSupport:
      cat.maxAmountSupport !== undefined && cat.maxAmountSupport !== null
        ? cat.maxAmountSupport
        : 0,
    tblTradeInHeaders: mappedHeaders,
    attribute1: cat.attribute1 || null,
  };
};

const SortableCriterionRow = ({
  criterion,
  headerId,
  onToggle,
  onStartEdit,
  onDelete,
}: {
  criterion: RuleCriterion;
  headerId: number | string;
  onToggle: (id: string | number, headerId: number | string) => void;
  onStartEdit: (
    id: string | number,
    text: string,
    headerId: number | string,
  ) => void;
  onDelete: (id: string | number, headerId: number | string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: String(criterion.id) });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: "#ffffff",
    border: "1px solid var(--mantine-color-gray-2)",
    borderRadius: "6px",
    marginBottom: "6px",
    gap: "8px",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: "grab",
          display: "flex",
          alignItems: "center",
          color: "var(--mantine-color-gray-4)",
        }}
      >
        <IconGripVertical size={16} />
      </span>

      <input
        type="checkbox"
        checked={criterion.isActive}
        onChange={() => onToggle(criterion.id, headerId)}
        style={{
          width: "16px",
          height: "16px",
          cursor: "pointer",
          accentColor: "var(--mantine-color-blue-6)",
        }}
      />

      <Text
        size="sm"
        style={{
          flex: 1,
          opacity: criterion.isActive ? 1 : 0.4,
          textDecoration: criterion.isActive ? "none" : "line-through",
          transition: "opacity 0.2s, text-decoration 0.2s",
        }}
      >
        {criterion.text}
      </Text>

      <Group gap={4} wrap="nowrap">
        <ActionIcon
          size="sm"
          variant="subtle"
          color="gray"
          onClick={() => onStartEdit(criterion.id, criterion.text, headerId)}
        >
          <IconEdit size={14} />
        </ActionIcon>
        <ActionIcon
          size="sm"
          variant="subtle"
          color="red"
          onClick={() => onDelete(criterion.id, headerId)}
        >
          <IconTrash size={14} />
        </ActionIcon>
      </Group>
    </div>
  );
};

const formatToSlug = (val: string): string => {
  return val
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const formatToSlugManual = (val: string): string => {
  return val
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "");
};

interface FormCategoryModalProps {
  opened: boolean;
  onClose: () => void;
  categoryId: string | number | null;
  categories: TradeCategoryRule[];
  onSave: () => void;
}

export const FormCategoryModal = ({
  opened,
  onClose,
  categoryId,
  categories,
  onSave,
}: FormCategoryModalProps) => {
  const [loading, setLoading] = useState(false);

  // Form States
  const [editingCategoryDetail, setEditingCategoryDetail] =
    useState<TradeCategoryRule | null>(null);
  const [formName, setFormName] = useState("");
  const [formLinkIndex, setFormLinkIndex] = useState("");
  const [formCategoryType, setFormCategoryType] = useState<
    "buyback" | "upgrade"
  >("buyback");
  const [formSupport, setFormSupport] = useState<number | string>("");
  const [formMaxAmount, setFormMaxAmount] = useState<number | string>("");
  const [formStatus, setFormStatus] = useState<"active" | "paused">("active");
  const [activeFormCondIndex, setActiveFormCondIndex] = useState<number>(0);
  const [formCondTab, setFormCondTab] = useState<string | null>("0");

  // Extended attribute form states
  const [formAttribute1, setFormAttribute1] = useState<string>("");

  const [formConditions, setFormConditions] = useState<
    {
      id: number;
      productCondition: string;
      percentSupport: number;
      tblTradeInLines: RuleCriterion[];
      shortDescription?: string;
      includedAccessories?: string;
      missingAccessoryNote?: string;
    }[]
  >([]);

  // Inner Modals State
  const [inlineInputs, setInlineInputs] = useState<Record<number, string>>({});

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCriterionId, setEditingCriterionId] = useState<string | number>(
    "",
  );
  const [editingCriterionText, setEditingCriterionText] = useState("");

  const getRelatedCategoryId = (
    id: string | number,
    type: "buyback" | "upgrade",
  ) => {
    const activeCat = categories.find((c) => String(c.id) === String(id));
    if (!activeCat) return "";

    const linkIndex = activeCat.linkIndex;
    if (type === "buyback") {
      let targetLink = linkIndex;
      if (linkIndex.endsWith("-old"))
        targetLink = linkIndex.replace("-old", "-new");
      else if (linkIndex.endsWith("old"))
        targetLink = linkIndex.substring(0, linkIndex.length - 3) + "new";
      else targetLink = linkIndex + "-new";

      return (
        categories.find(
          (c) => c.linkIndex === targetLink && c.categoryType === "upgrade",
        )?.id || ""
      );
    } else {
      let targetLink = linkIndex;
      if (linkIndex.endsWith("-new"))
        targetLink = linkIndex.replace("-new", "-old");
      else if (linkIndex.endsWith("new"))
        targetLink = linkIndex.substring(0, linkIndex.length - 3) + "old";
      else targetLink = linkIndex + "-old";

      return (
        categories.find(
          (c) => c.linkIndex === targetLink && c.categoryType === "buyback",
        )?.id || ""
      );
    }
  };

  useEffect(() => {
    if (!opened) return;

    const initForm = async () => {
      setLoading(true);
      try {
        if (categoryId) {
          // Editing mode
          const response = await getCategoryTradeInSettingDetail(categoryId);
          if (response && response.success && response.data) {
            const detailedCat = mapCategoryResponseToState(response.data);

            setEditingCategoryDetail(detailedCat);
            setFormName(detailedCat.groupName);
            setFormLinkIndex(detailedCat.linkIndex);
            const isUpgrade =
              detailedCat.type === "LÊN ĐỜI" ||
              detailedCat.type === "LEN DOI" ||
              detailedCat.categoryType === "upgrade";
            setFormCategoryType(isUpgrade ? "upgrade" : "buyback");

            let buybackHeaders = isUpgrade
              ? []
              : detailedCat.headers.filter((h) => h.option === 1);
            if (isUpgrade && buybackHeaders.length === 0) {
              const buybackId = getRelatedCategoryId(detailedCat.id, "upgrade");
              const buybackCat = categories.find(
                (c) => String(c.id) === String(buybackId),
              );
              if (buybackCat) {
                buybackHeaders = buybackCat.headers
                  .filter((h) => h.option === 1)
                  .map((h) => ({
                    id: 0,
                    option: 1,
                    productCondition: h.productCondition,
                    percentSupport: h.percentSupport,
                    amountSupport: null,
                    shortDescription: "",
                    includedAccessories: "",
                    missingAccessoryNote: "",
                    tblTradeInLines: [],
                  }));
              }
            }

            const templateCat = categories.find(
              (c) => c.headers && c.headers.some((h) => h.option === 1),
            );
            const initialConditions = [...buybackHeaders];
            while (initialConditions.length < 4) {
              const nextIdx = initialConditions.length + 1;
              const defaultCondition = templateCat?.headers.filter(
                (h) => h.option === 1,
              )[initialConditions.length];

              initialConditions.push({
                id: 0,
                option: 1,
                productCondition:
                  defaultCondition?.productCondition || `Loại ${nextIdx}`,
                percentSupport:
                  defaultCondition?.percentSupport !== undefined
                    ? defaultCondition.percentSupport
                    : 0,
                amountSupport: null,
                shortDescription: "",
                includedAccessories: "",
                missingAccessoryNote: "",
                tblTradeInLines: [],
              });
            }

            setFormConditions(
              initialConditions.map((h) => ({
                id: h.id,
                productCondition: h.productCondition,
                percentSupport: h.percentSupport,
                tblTradeInLines: h.tblTradeInLines,
                shortDescription: h.shortDescription || "",
                includedAccessories: h.includedAccessories || "",
                missingAccessoryNote: h.missingAccessoryNote || "",
              })),
            );
            setFormCondTab("0");

            const upHeader = detailedCat.headers.find((h) => h.option === 2);
            setFormSupport(
              isUpgrade
                ? detailedCat.percentSupport !== undefined &&
                  detailedCat.percentSupport !== null
                  ? detailedCat.percentSupport
                  : upHeader
                    ? upHeader.percentSupport
                    : ""
                : ""
            );
            setFormMaxAmount(
              detailedCat.maxAmountSupport !== undefined &&
                detailedCat.maxAmountSupport !== null
                ? detailedCat.maxAmountSupport
                : isUpgrade
                  ? upHeader
                    ? upHeader.amountSupport || ""
                    : ""
                  : ""
            );

            setFormStatus(detailedCat.status === "1" ? "active" : "paused");
            setFormAttribute1(detailedCat.attribute1 || "");
          } else {
            NotificationExtension.Fails("Không thể tải chi tiết ngành hàng!");
            onClose();
          }
        } else {
          // Adding mode
          setEditingCategoryDetail(null);
          setFormName("");
          setFormLinkIndex("");
          setFormCategoryType("buyback");

          const templateCat = categories.find(
            (c) => c.headers && c.headers.some((h) => h.option === 1),
          );
          const defaultConditions = templateCat
            ? templateCat.headers
                .filter((h) => h.option === 1)
                .map((h) => ({
                  id: 0,
                  productCondition: h.productCondition,
                  percentSupport: h.percentSupport,
                  tblTradeInLines: [],
                  shortDescription: "",
                  includedAccessories: "",
                  missingAccessoryNote: "",
                }))
            : [
                {
                  id: 0,
                  productCondition: "Loại 1",
                  percentSupport: 55,
                  tblTradeInLines: [],
                  shortDescription: "",
                  includedAccessories: "",
                  missingAccessoryNote: "",
                },
                {
                  id: 0,
                  productCondition: "Loại 2",
                  percentSupport: 45,
                  tblTradeInLines: [],
                  shortDescription: "",
                  includedAccessories: "",
                  missingAccessoryNote: "",
                },
                {
                  id: 0,
                  productCondition: "Loại 3",
                  percentSupport: 35,
                  tblTradeInLines: [],
                  shortDescription: "",
                  includedAccessories: "",
                  missingAccessoryNote: "",
                },
                {
                  id: 0,
                  productCondition: "Loại 4",
                  percentSupport: 25,
                  tblTradeInLines: [],
                  shortDescription: "",
                  includedAccessories: "",
                  missingAccessoryNote: "",
                },
              ];

          setFormConditions(defaultConditions);
          setFormCondTab("0");
          setFormSupport("");
          setFormMaxAmount("");
          setFormStatus("active");
          setFormAttribute1("");
        }
      } catch (err) {
        console.error("Error loading category details in Form:", err);
        NotificationExtension.Fails("Lỗi tải thông tin chi tiết!");
        onClose();
      } finally {
        setLoading(false);
      }
    };

    initForm();
  }, [opened, categoryId]);

  const handleFormDragEnd = (
    event: DragEndEvent,
    headerId: number | string,
  ) => {
    const condIndex = Number(headerId);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setFormConditions((prev) => {
      const updated = [...prev];
      const items = [...updated[condIndex].tblTradeInLines];
      const oldIndex = items.findIndex(
        (item) => String(item.id) === String(active.id),
      );
      const newIndex = items.findIndex(
        (item) => String(item.id) === String(over.id),
      );
      if (oldIndex === -1 || newIndex === -1) return prev;
      const [moved] = items.splice(oldIndex, 1);
      items.splice(newIndex, 0, moved);
      updated[condIndex] = {
        ...updated[condIndex],
        tblTradeInLines: items,
      };
      return updated;
    });
  };

  const handleFormToggleCriterion = (
    id: string | number,
    headerId: number | string,
  ) => {
    const condIndex = Number(headerId);
    setFormConditions((prev) => {
      const updated = [...prev];
      updated[condIndex] = {
        ...updated[condIndex],
        tblTradeInLines: updated[condIndex].tblTradeInLines.map((item) =>
          String(item.id) === String(id)
            ? { ...item, isActive: !item.isActive }
            : item,
        ),
      };
      return updated;
    });
  };

  const handleFormStartEditCriterion = (
    criterionId: string | number,
    text: string,
    headerId: number | string,
  ) => {
    const condIndex = Number(headerId);
    setEditingCriterionId(criterionId);
    setActiveFormCondIndex(condIndex);
    setEditingCriterionText(text);
    setEditModalOpen(true);
  };

  const handleFormDeleteCriterion = (
    id: string | number,
    headerId: number | string,
  ) => {
    const condIndex = Number(headerId);
    setFormConditions((prev) => {
      const updated = [...prev];
      updated[condIndex] = {
        ...updated[condIndex],
        tblTradeInLines: updated[condIndex].tblTradeInLines.filter(
          (item) => String(item.id) !== String(id),
        ),
      };
      return updated;
    });
    NotificationExtension.Success("Đã xóa tiêu chí đánh giá!");
  };

  const handleInlineAddCriterion = (condIndex: number) => {
    const text = inlineInputs[condIndex] || "";
    if (!text.trim()) {
      NotificationExtension.Warn("Vui lòng nhập nội dung tiêu chí!");
      return;
    }
    const newId = `criterion-${Date.now()}`;
    setFormConditions((prev) => {
      const updated = [...prev];
      if (updated[condIndex]) {
        updated[condIndex] = {
          ...updated[condIndex],
          tblTradeInLines: [
            ...updated[condIndex].tblTradeInLines,
            { id: newId, text: text.trim(), isActive: true },
          ],
        };
      }
      return updated;
    });
    setInlineInputs((prev) => ({
      ...prev,
      [condIndex]: "",
    }));
    NotificationExtension.Success("Đã thêm tiêu chí đánh giá mới!");
  };

  const handleConfirmEditCriterion = () => {
    if (!editingCriterionText.trim()) {
      NotificationExtension.Warn("Nội dung tiêu chí không được để trống!");
      return;
    }

    const condIndex = Number(activeFormCondIndex);
    setFormConditions((prev) => {
      const updated = [...prev];
      if (updated[condIndex]) {
        updated[condIndex] = {
          ...updated[condIndex],
          tblTradeInLines: updated[condIndex].tblTradeInLines.map((item) =>
            String(item.id) === String(editingCriterionId)
              ? { ...item, text: editingCriterionText }
              : item,
          ),
        };
      }
      return updated;
    });
    setEditModalOpen(false);
    NotificationExtension.Success("Đã cập nhật nội dung tiêu chí!");
  };

  const handleSaveForm = async () => {
    if (!formName.trim()) {
      NotificationExtension.Warn("Vui lòng điền tên ngành hàng!");
      return;
    }
    if (!formLinkIndex.trim()) {
      NotificationExtension.Warn("Vui lòng điền mã ngành hàng!");
      return;
    }

    if (formMaxAmount !== "" && formMaxAmount !== null && formMaxAmount !== undefined && Number(formMaxAmount) <= 0) {
      NotificationExtension.Warn("Giá trị Giới hạn hỗ trợ tối đa phải lớn hơn 0!");
      return;
    }


    setLoading(true);
    try {
      const isUpgrade = formCategoryType === "upgrade";
      const newHeaders: TradeCategoryHeader[] = [];

      if (isUpgrade) {
        // Clear all headers for LEN DOI category type
      } else {
        formConditions.forEach((cond) => {
          let existingHeader: any = null;
          if (editingCategoryDetail) {
            existingHeader = editingCategoryDetail.headers.find(
              (h) =>
                h.id === cond.id ||
                h.productCondition === cond.productCondition,
            );
          }

          newHeaders.push({
            id: cond.id || (existingHeader ? existingHeader.id : 0),
            option: 1,
            productCondition: cond.productCondition,
            percentSupport: cond.percentSupport,
            amountSupport: null,
            shortDescription: cond.shortDescription || "",
            includedAccessories: cond.includedAccessories || "",
            missingAccessoryNote: cond.missingAccessoryNote || "",
            tblTradeInLines: cond.tblTradeInLines || [],
          });
        });
      }

      if (editingCategoryDetail) {
        const updatedCat = {
          ...editingCategoryDetail,
          groupName: formName,
          linkIndex: formLinkIndex,
          type: isUpgrade ? "LÊN ĐỜI" : "THU MUA",
          status: formStatus === "active" ? "1" : "0",
          percentSupport: isUpgrade ? (formSupport === "" ? null : Number(formSupport)) : null,
          maxAmountSupport: formMaxAmount === "" ? 0 : Number(formMaxAmount),
          headers: newHeaders,
          attribute1: formAttribute1 || null,
        };
        const payload = mapStateToSavePayload(updatedCat);
        const res = await editCategoryTradeInSetting(payload);
        if (res && res.success) {
          NotificationExtension.Success("Cập nhật ngành hàng thành công!");
          onSave();
        }
      } else {
        const newCat: TradeCategoryRule = {
          id: 0,
          groupName: formName,
          linkIndex: formLinkIndex,
          name: formName,
          type: isUpgrade ? "LÊN ĐỜI" : "THU MUA",
          categoryType: formCategoryType,
          status: formStatus === "active" ? "1" : "0",
          percentSupport: isUpgrade ? (formSupport === "" ? null : Number(formSupport)) : null,
          maxAmountSupport: formMaxAmount === "" ? 0 : Number(formMaxAmount),
          orderNumber: 1,
          headers: newHeaders,
          attribute1: formAttribute1 || null,
        };
        const payload = mapStateToSavePayload(newCat);
        const res = await createCategoryTradeInSetting(payload);
        if (res && res.success) {
          NotificationExtension.Success("Thêm mới ngành hàng thành công!");
          onSave();
        }
      }
    } catch (error) {
      console.error("Error saving category info:", error);
      NotificationExtension.Fails("Lưu cấu hình thất bại!");
    } finally {
      setLoading(false);
    }
  };

  const categoriesList = useMemo(() => {
    return categories
      .filter((c) => String(c.id) !== String(categoryId))
      .map((c) => ({ value: String(c.id), label: c.groupName }));
  }, [categories, categoryId]);

  return (
    <>
      <style>{`
        /* Style scrollbars inside FormCategoryModal */
        .modal-scroll-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .modal-scroll-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .modal-scroll-container::-webkit-scrollbar-thumb {
          background: var(--mantine-color-gray-3);
          border-radius: 10px;
        }
        .modal-scroll-container::-webkit-scrollbar-thumb:hover {
          background: var(--mantine-color-gray-5);
        }

        /* Firefox support */
        .modal-scroll-container {
          scrollbar-width: thin;
          scrollbar-color: var(--mantine-color-gray-3) transparent;
        }
      `}</style>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Title order={5} fw={700}>
            {categoryId ? `Chỉnh sửa: ${formName}` : "Thêm mới ngành hàng"}
          </Title>
        }
        centered
        styles={{
          content: {
            width: "1000px",
            minWidth: "1000px",
            maxWidth: "95vw",
            height: "90vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
          body: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: 0,
          },
        }}
      >
        <Box
          style={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: "100%",
          }}
        >
          <LoadingOverlay visible={loading} zIndex={1000} />

          <Box
            className="modal-scroll-container"
            style={{
              flex: 1,
              overflowY: "auto",
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "16px",
              paddingBottom: "8px",
            }}
          >
            <Stack gap="md">
              {categoryId && (
                <TextInput
                  label="Mã ID ngành hàng (Hệ thống)"
                  value={categoryId}
                  disabled
                  variant="filled"
                />
              )}

              <TextInput
                label="Tên ngành hàng"
                placeholder="Ví dụ: Laptop cũ, Laptop mới..."
                required
                value={formName}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  setFormName(val);
                  if (!categoryId) {
                    setFormLinkIndex(formatToSlug(val));
                  }
                }}
              />

              <TextInput
                label="Mã ngành hàng (Url slug)"
                placeholder="Ví dụ: laptop-old"
                required
                value={formLinkIndex}
                onChange={(e) =>
                  setFormLinkIndex(formatToSlugManual(e.currentTarget.value))
                }
              />

              <TextInput
                label="Số thứ tự"
                placeholder="Nhập số thứ tự"
                value={formAttribute1}
                onChange={(e) => setFormAttribute1(e.currentTarget.value)}
              />

              <Select
                label="Loại hình áp dụng"
                required
                data={[
                  { value: "buyback", label: "Thu mua (Máy cũ)" },
                  { value: "upgrade", label: "LÊN ĐỜI (Máy mới trợ giá)" },
                ]}
                value={formCategoryType}
                onChange={(val) => {
                  const type = val as "buyback" | "upgrade";
                  setFormCategoryType(type);
                }}
              />

              {formCategoryType === "buyback" && (
                <Paper p="sm" withBorder radius="md">
                  <Group justify="space-between" mb="xs">
                    <Text fw={600} size="sm">
                      Cấu hình các loại thu mua
                    </Text>
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="outline"
                        leftSection={<IconPlus size={12} />}
                        onClick={() => {
                          const newIndex = formConditions.length + 1;
                          setFormConditions([
                            ...formConditions,
                            {
                              id: 0,
                              productCondition: `Loại ${newIndex}`,
                              percentSupport: 50,
                              tblTradeInLines: [],
                              shortDescription: "",
                              includedAccessories: "",
                              missingAccessoryNote: "",
                            },
                          ]);
                          setFormCondTab(String(formConditions.length));
                        }}
                      >
                        Thêm loại
                      </Button>
                    </Group>
                  </Group>

                  <Tabs value={formCondTab} onChange={setFormCondTab}>
                    <Tabs.List>
                      {formConditions.map((cond, index) => (
                        <Tabs.Tab
                          key={index}
                          value={String(index)}
                          rightSection={
                            formConditions.length > 1 && (
                              <ActionIcon
                                size="xs"
                                color="red"
                                variant="subtle"
                                style={{ borderRadius: "50%" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updated = formConditions.filter(
                                    (_, idx) => idx !== index,
                                  );
                                  setFormConditions(updated);
                                  setFormCondTab("0");
                                }}
                              >
                                <IconX size={10} />
                              </ActionIcon>
                            )
                          }
                        >
                          {cond.productCondition} ({cond.percentSupport}%)
                        </Tabs.Tab>
                      ))}
                    </Tabs.List>

                    {formConditions.map((cond, index) => (
                      <Tabs.Panel key={index} value={String(index)} pt="md">
                        <Grid gutter="md">
                          {/* Cột trái: Tiêu chí */}
                          {/* <Grid.Col span={6}>
                          <Stack gap="xs">
                            <Group justify="space-between" align="center">
                              <Text fw={600} size="xs" c="gray.7">
                                Tiêu chí đánh giá cho {cond.productCondition}
                              </Text>
                              {formConditions.length > 1 && (
                                <Button
                                  size="compact-xs"
                                  color="red"
                                  variant="light"
                                  leftSection={<IconTrash size={12} />}
                                  onClick={() => {
                                    const updated = formConditions.filter((_, idx) => idx !== index);
                                    setFormConditions(updated);
                                    setFormCondTab("0");
                                  }}
                                >
                                  Xóa loại này
                                </Button>
                              )}
                            </Group>

                            <Box style={{ minHeight: 180, maxHeight: 300, overflowY: "auto", border: "1px solid var(--mantine-color-gray-2)", borderRadius: "6px", padding: "8px", backgroundColor: "var(--mantine-color-gray-0)" }}>
                              <DndContext onDragEnd={(e) => handleFormDragEnd(e, index)}>
                                <SortableContext items={cond.tblTradeInLines.map((c) => String(c.id))}>
                                  {cond.tblTradeInLines.map((criterion) => (
                                    <SortableCriterionRow
                                      key={criterion.id}
                                      criterion={criterion}
                                      headerId={index}
                                      onToggle={handleFormToggleCriterion}
                                      onStartEdit={handleFormStartEditCriterion}
                                      onDelete={handleFormDeleteCriterion}
                                    />
                                  ))}
                                </SortableContext>
                              </DndContext>

                              <Group gap="xs" mt="xs" wrap="nowrap">
                                <TextInput
                                  placeholder="Nhập tiêu chí mới..."
                                  size="xs"
                                  style={{ flex: 1 }}
                                  value={inlineInputs[index] || ""}
                                  onChange={(e) => {
                                    setInlineInputs((prev) => ({
                                      ...prev,
                                      [index]: e.currentTarget.value
                                    }));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleInlineAddCriterion(index);
                                    }
                                  }}
                                />
                                <ActionIcon
                                  variant="light"
                                  color="blue"
                                  size="md"
                                  onClick={() => handleInlineAddCriterion(index)}
                                  title="Thêm tiêu chí"
                                >
                                  <IconPlus size={16} />
                                </ActionIcon>
                              </Group>
                            </Box>
                          </Stack>
                        </Grid.Col> */}

                          {/* Cột phải: Thông tin nhập */}
                          <Grid.Col span={12}>
                            <Stack gap="xs">
                              <Group grow gap="xs">
                                <TextInput
                                  label="Tên loại"
                                  placeholder="Ví dụ: Loại 1"
                                  value={cond.productCondition}
                                  required
                                  onChange={(e) => {
                                    const updated = [...formConditions];
                                    updated[index].productCondition =
                                      e.currentTarget.value;
                                    setFormConditions(updated);
                                  }}
                                />
                                <NumberInput
                                  label="Tỷ lệ (%)"
                                  placeholder="Tỷ lệ"
                                  min={0}
                                  max={100}
                                  suffix=" %"
                                  required
                                  value={cond.percentSupport}
                                  onChange={(val) => {
                                    const updated = [...formConditions];
                                    updated[index].percentSupport =
                                      typeof val === "number" ? val : 0;
                                    setFormConditions(updated);
                                  }}
                                />
                              </Group>

                              <Textarea
                                label="Mô tả ngắn gọn (Tình trạng vật lý)"
                                placeholder="Mô tả tình trạng..."
                                value={cond.shortDescription || ""}
                                onChange={(e) => {
                                  const updated = [...formConditions];
                                  updated[index].shortDescription =
                                    e.currentTarget.value;
                                  setFormConditions(updated);
                                }}
                                minRows={2}
                                autosize
                                size="sm"
                              />

                              <Textarea
                                label="Phụ kiện đi kèm"
                                placeholder="Các phụ kiện đi kèm..."
                                value={cond.includedAccessories || ""}
                                onChange={(e) => {
                                  const updated = [...formConditions];
                                  updated[index].includedAccessories =
                                    e.currentTarget.value;
                                  setFormConditions(updated);
                                }}
                                minRows={2}
                                autosize
                                size="sm"
                              />

                              <Textarea
                                label="Chi phí khi thiếu phụ kiện / Ghi chú"
                                placeholder="Mức trừ phí hoặc ghi chú..."
                                value={cond.missingAccessoryNote || ""}
                                onChange={(e) => {
                                  const updated = [...formConditions];
                                  updated[index].missingAccessoryNote =
                                    e.currentTarget.value;
                                  setFormConditions(updated);
                                }}
                                minRows={2}
                                autosize
                                size="sm"
                              />
                            </Stack>
                          </Grid.Col>
                        </Grid>
                      </Tabs.Panel>
                    ))}
                  </Tabs>
                </Paper>
              )}

              <NumberInput
                label="Hỗ trợ thu cũ đổi mới (%)"
                description="Hỗ trợ thêm khi khách bán cũ và mua mới"
                required
                min={0}
                max={100}
                suffix=" %"
                disabled={formCategoryType === "buyback"}
                value={formSupport}
                onChange={(val) =>
                  setFormSupport(typeof val === "number" ? val : "")
                }
              />

              <NumberInput
                label="Giới hạn hỗ trợ tối đa (đồng)"
                description="Số tiền hỗ trợ tối đa cho mỗi giao dịch"
                min={0}
                thousandSeparator="."
                decimalSeparator=","
                value={formMaxAmount}
                onChange={(val) =>
                  setFormMaxAmount(typeof val === "number" ? val : "")
                }
              />

              <Select
                label="Trạng thái"
                data={[
                  { value: "active", label: "Đang áp dụng" },
                  { value: "paused", label: "Tạm dừng" },
                ]}
                value={formStatus}
                onChange={(val) => setFormStatus(val as any)}
              />
            </Stack>
          </Box>

          <Box
            style={{
              borderTop: "1px solid var(--mantine-color-gray-2)",
              paddingTop: "12px",
              paddingBottom: "16px",
              paddingLeft: "16px",
              paddingRight: "16px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              backgroundColor: "#ffffff",
              zIndex: 10,
            }}
          >
            <Button variant="outline" color="gray" onClick={onClose}>
              Hủy
            </Button>
            <Button color="blue" onClick={handleSaveForm}>
              Lưu
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal: Edit Criterion */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={<Text fw={700}>Chỉnh sửa tiêu chí đánh giá</Text>}
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Nội dung tiêu chí"
            value={editingCriterionText}
            onChange={(e) => setEditingCriterionText(e.currentTarget.value)}
            required
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditModalOpen(false)}>
              Hủy
            </Button>
            <Button onClick={handleConfirmEditCriterion} color="blue">
              Lưu thay đổi
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
